import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInstagramLoginAnalytics } from '../../Redux/adminSlice';
import ContentLoader from '../utils/ContentLoader';
import {
  FiTrendingUp,
  FiUsers,
  FiRefreshCw,
  FiGlobe,
  FiMapPin
} from 'react-icons/fi';
import { X } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#F87171', '#60A5FA', '#A78BFA', '#34D399', '#FBBF24'];

const InstagramAnalytics = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.admin.instagramAnalytics);
  const [filterType, setFilterType] = useState('country');  // 'country' | 'city' | 'date'  
  const [showLocationModal, setShowLocationModal] = useState(false);

  useEffect(() => {
    dispatch(fetchInstagramLoginAnalytics());
  }, [dispatch]);

  // Process location data
  const processLocationData = () => {
    if (!data || !data.locations) return { countryData: [], cityData: [] };
    
    const countryMap = {};
    const cityMap = {};

    (data.locations || []).forEach((loc) => {
      const count = loc.count || 1;

      countryMap[loc.country] = (countryMap[loc.country] || 0) + count;

      const cityKey = `${loc.country}-${loc.city}`;
      if (!cityMap[cityKey]) {
        cityMap[cityKey] = { country: loc.country, city: loc.city, count: 0 };
      }
      cityMap[cityKey].count += count;
    });

    return {
      countryData: Object.entries(countryMap)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10),

      cityData: Object.values(cityMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 15)
    };
  };

  // ✅ ADDED: date-wise processing
  const processDateData = () => {
    if (!data || !data.locations || data.locations.length === 0) return [];

    const dateMap = {};

    data.locations.forEach(loc => {
      const dateObj = new Date(loc.createdAt || loc.timestamp);
      if (isNaN(dateObj)) return;

      const dateKey = dateObj.toLocaleDateString();
      dateMap[dateKey] = (dateMap[dateKey] || 0) + (loc.count || 1);
    });

    return Object.entries(dateMap)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const { countryData, cityData } = data ? processLocationData() : { countryData: [], cityData: [] };
  const dateData = data ? processDateData() : [];

  const conversionRate =
    data && data.totalClicks > 0
      ? ((data.uniqueClicks / data.totalClicks) * 100).toFixed(1)
      : 0;


  // Get detailed location data for modal
  const getLocationDetails = () => {
    if (!data || !data.locations || data.locations.length === 0) return [];

    return data.locations.map((loc, index) => ({
      id: index,
      country: loc.country || 'Unknown',
      city: loc.city || 'Unknown',
      region: loc.region || 'Unknown',
      ip: loc.ip || 'Unknown',
      timestamp: loc.timestamp || loc.createdAt || null
    })).sort((a, b) => {
      // Sort by timestamp descending (most recent first)
      const dateA = new Date(a.timestamp || 0);
      const dateB = new Date(b.timestamp || 0);
      return dateB - dateA;
    });
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

          {/* Title & description */}
          <div className="max-w-full">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-800 whitespace-nowrap">
              Instagram Login Analytics
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Track Instagram login button clicks and user engagement
            </p>
          </div>
          {/* Refresh button */}
          <div className="flex justify-end sm:justify-start">
            <button
              onClick={() => dispatch(fetchInstagramLoginAnalytics())}
              className="px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-md 
                   hover:bg-orange-600 transition-colors flex items-center gap-2
                   text-sm sm:text-base"
            >
              <FiRefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

        </div>
      </div>

      {/* Show loader ONLY when refreshing / fetching */}
      {loading && <ContentLoader message="Refreshing analytics..." />}

      {/* Error UI */}
      {!loading && error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-md mb-4">
          <h3 className="text-lg font-medium">Error loading analytics data</h3>
          <p>{error}</p>
          <button
            onClick={() => dispatch(fetchInstagramLoginAnalytics())}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Page Content */}
      {!loading && !error && (
        <>
          {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Clicks"
          value={data?.totalClicks || 0}
          icon={<FiTrendingUp className="w-8 h-8" />}
          color="bg-blue-500"
          description="All Instagram login button clicks"
        />
        <StatCard
          title="Unique Users"
          value={data?.uniqueClicks || 0}
          icon={<FiUsers className="w-8 h-8" />}
          color="bg-green-500"
          description="Unique users who clicked"
        />
        <StatCard
          title="Conversion Rate"
          value={`${conversionRate}%`}
          icon={<FiTrendingUp className="w-8 h-8" />}
          color="bg-purple-500"
          description="Unique users / Total clicks"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Country Distribution Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="lg:text-xl text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FiGlobe className="w-5 h-5" />
              Clicks by Country
            </h2>
          </div>
          {countryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={countryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="country"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#0088FE" name="Clicks" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No country data available
            </div>
          )}
        </div>

        {/* Country Pie Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="lg:text-xl text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FiGlobe className="w-5 h-5" />
              Country Distribution
            </h2>
          </div>
          {countryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ country, percent }) => `${country}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {countryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No country data available
            </div>
          )}
        </div>
      </div>

      {/* City Breakdown Table */}
      {/* City / Location Breakdown Table */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">

          {/* Title */}
          <h2 className="lg:text-xl text-lg font-semibold text-gray-800 flex items-center gap-2 whitespace-nowrap">
            <FiMapPin className="w-5 h-5" />
            Location Breakdown
          </h2>

          <div className="flex gap-2 self-end sm:self-auto">

            {/* ✅ Filter Dropdown (NEW) */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1 rounded-md text-sm bg-gray-100 border 
                   border-gray-300 text-gray-700 focus:outline-none 
                   focus:ring-2 focus:ring-orange-500"
            >
              <option value="country">By Country</option>
              <option value="city">By City</option>
              <option value="date">By Date</option>
            </select>

            {/* Location Details Button (UNCHANGED) */}
            <button
              onClick={() => setShowLocationModal(true)}
              className="px-3 py-1 rounded-md text-sm bg-orange-500 text-white 
                   hover:bg-orange-600 transition-colors flex items-center gap-1"
            >
              <FiMapPin className="w-4 h-4" />
              View Details
            </button>
          </div>
        </div>

        {/* ================= TABLE RENDERING ================= */}

        {/* ✅ COUNTRY TABLE */}
        {filterType === 'country' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {countryData.map((item, index) => {
                  const percentage = data && data.totalClicks > 0 ? ((item.count / data.totalClicks) * 100).toFixed(2) : 0;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.country}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {percentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ✅ CITY TABLE */}
        {filterType === 'city' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cityData.map((item, index) => {
                  const percentage = data && data.totalClicks > 0 ? ((item.count / data.totalClicks) * 100).toFixed(2) : 0;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.country}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.city}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {percentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ✅ DATE TABLE (NEW) */}
        {filterType === 'date' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Clicks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dateData.map((item, index) => {
                  const percentage = data && data.totalClicks > 0 ? ((item.count / data.totalClicks) * 100).toFixed(2) : 0;
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.date}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {percentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {countryData.length === 0 && cityData.length === 0 && dateData.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No location data available
          </div>
        )}
      </div>
        </>
      )}

      {/* No data state */}
      {!loading && !error && !data && (
        <div className="text-center py-12">
          <p className="text-gray-500">No analytics data available yet.</p>
          <button
            onClick={() => dispatch(fetchInstagramLoginAnalytics())}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Location Details Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FiMapPin className="text-orange-500" />
                Location Details
              </h3>
              <button
                onClick={() => setShowLocationModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Total Clicks: <span className="font-bold">{data?.totalClicks || 0}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Unique Users: <span className="font-bold">{data?.uniqueClicks || 0}</span>
                </p>
              </div>
              {getLocationDetails().length === 0 ? (
                <p className="text-gray-500 text-center py-8">No location data available yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border px-4 py-2 text-left text-sm font-semibold">#</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">Country</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">Region</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">City</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">IP Address</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">Date</th>
                        <th className="border px-4 py-2 text-left text-sm font-semibold">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.locations ? getLocationDetails() : []).map((location, index) => {
                        const date = location.timestamp ? new Date(location.timestamp) : null;
                        const dateStr = date ? date.toLocaleDateString() : 'N/A';
                        const timeStr = date ? date.toLocaleTimeString() : 'N/A';

                        return (
                          <tr key={location.id} className="hover:bg-gray-50">
                            <td className="border px-4 py-2 text-sm">{index + 1}</td>
                            <td className="border px-4 py-2 text-sm">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                {location.country}
                              </span>
                            </td>
                            <td className="border px-4 py-2 text-sm">{location.region}</td>
                            <td className="border px-4 py-2 text-sm">{location.city}</td>
                            <td className="border px-4 py-2 text-sm font-mono text-xs">{location.ip}</td>
                            <td className="border px-4 py-2 text-sm">{dateStr}</td>
                            <td className="border px-4 py-2 text-sm">{timeStr}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, description }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
          {description && (
            <p className="text-xs text-gray-400 mt-1">{description}</p>
          )}
        </div>
        <div className={`${color} p-4 rounded-lg text-white`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default InstagramAnalytics;
